import React, { useState, useRef, useEffect, useCallback } from 'react';
import PlayAudio from './PlayAudio';
import GameIcon from './GameIcon';

// ── Personality config (unchanged from original) ──
const PERSONALITY_DEFAULTS = {
  friendly:  { startMult: 1.3, dropEasy: 0.15, dropHard: 0.25, moodLoss: 5, walkSuccessRate: 0.4 },
  tough:     { startMult: 1.1, dropEasy: 0.05, dropHard: 0.12, moodLoss: 15, walkSuccessRate: 0.3 },
  tricky:    { startMult: 1.25, dropEasy: 0.08, dropHard: 0.18, moodLoss: 10, walkSuccessRate: 0.5 },
  generous:  { startMult: 1.15, dropEasy: 0.12, dropHard: 0.22, moodLoss: 8, walkSuccessRate: 0.6 },
};

function buildPersonality(merchant) {
  const defaults = PERSONALITY_DEFAULTS[merchant.personality] || PERSONALITY_DEFAULTS.friendly;
  const sensitivity = (merchant.mood_sensitivity || 5) / 5;
  return {
    startMult: merchant.start_price_multiplier || defaults.startMult,
    dropEasy: defaults.dropEasy,
    dropHard: defaults.dropHard,
    moodLoss: Math.round(defaults.moodLoss * sensitivity),
    walkSuccessRate: merchant.walk_away_success_rate != null
      ? merchant.walk_away_success_rate / 100
      : defaults.walkSuccessRate,
  };
}

// ── Conversation data ──
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const fillTemplate = (t, vars) => {
  let s = t;
  Object.entries(vars).forEach(([k, v]) => { s = s.replace(new RegExp(`\\{${k}\\}`, 'g'), v); });
  return s;
};

const DEFAULT_OPENINGS = {
  friendly: [
    'Ahlan habib! This {item} is only {price} tokens — best quality in the souk!',
    'Marhba marhba! You have excellent taste! {item} — {price} tokens!',
    'Welcome my friend! The {item}? Only {price} tokens today!',
  ],
  tough: [
    '{price} tokens for the {item}. Best price you\'ll find.',
    'You want the {item}? {price}. That\'s my price.',
    'The {item}. {price} tokens. Non-negotiable.',
  ],
  tricky: [
    'Ahhh you have EXCELLENT taste! This {item}? For you, special: {price} tokens!',
    'Look at this beauty! Only {price} tokens... and I\'m being generous!',
    'Everyone wants this {item}! Quick, before someone else takes it — {price}!',
  ],
  generous: [
    'Welcome! This {item} is {price} tokens — but let\'s talk, habib!',
    'Marhba! {price} tokens for the {item} — but I\'m flexible!',
    'Ah, the {item}! {price} tokens... but I like your face. Let\'s negotiate!',
  ],
};

// Merchant responses keyed by [choiceType][personality]
const DEFAULT_RESPONSES = {
  gentle: {
    friendly: [
      'Shukran habib! Because you\'re kind — {price} tokens!',
      'You appreciate quality! Okay — {price}!',
      'Ahh, you flatter me! {price} tokens, special!',
    ],
    tough: [
      'Sweet words don\'t change prices. {price}.',
      'Hmph. Since you asked nicely... {price}.',
      'Fine. {price}. Don\'t push it.',
    ],
    tricky: [
      'Such charm! But business is business — {price}!',
      'Beautiful words! How about {price}? Fair!',
    ],
    generous: [
      'You\'re so kind! {price} tokens for you!',
      'A person of taste! {price} — happy?',
    ],
  },
  moderate: {
    friendly: [
      'You drive a fair bargain! {price}, habib!',
      'Okay okay, middle ground — {price}!',
      'Hmm, let\'s find a fair price... {price}!',
    ],
    tough: [
      'You want to negotiate? Fine. {price}.',
      '{price}. Don\'t ask again.',
    ],
    tricky: [
      'Interesting approach! How about {price}?',
      'I see you know the game! {price}!',
    ],
    generous: [
      'Fair point! {price} — everyone happy!',
      'You negotiate well! {price} tokens!',
    ],
  },
  aggressive: {
    friendly: [
      'Oof! You wound me! But okay — {price}!',
      'Astaghfirullah! Fine FINE — {price}!',
      'You\'re tough! Okay — {price}, don\'t tell anyone!',
    ],
    tough: [
      'You dare?! ...{price}. Not one token less!',
      'I should throw you out! But... {price}. FINAL!',
    ],
    tricky: [
      'Such drama! I respect that! {price}!',
      'You play hard! Okay — {price}. But careful!',
    ],
    generous: [
      'You\'re tough! Fine — {price}! I\'ll eat bread tonight!',
      'Wallah you\'ll bankrupt me! {price}. Mercy!',
    ],
  },
  counter: {
    friendly: [
      '{counter}?! Habib, be reasonable! How about {price}?',
      '{counter}? Almost! Meet me at {price}?',
    ],
    tough: [
      '{counter}?! You insult me! {price}. Period.',
      'Never! {counter}?! {price}. Take it.',
    ],
    tricky: [
      'Hmm, {counter}... creative! What about {price}?',
      '{counter}? I was thinking more like {price}...',
    ],
    generous: [
      '{counter}? Not quite, but close! {price}?',
      'Almost! Can\'t do {counter}, but {price} — deal?',
    ],
  },
};

// Late-round variants (rounds 2-3, more urgency)
const LATE_RESPONSES = {
  gentle: {
    friendly: 'Okay, LAST drop — {price}. Wallah I can\'t go lower!',
    tough: '{price}. This is my LAST word.',
    tricky: 'Fine fine! {price}! You\'ve beaten me!',
    generous: '{price}. Already losing money. Please, deal?',
  },
  aggressive: {
    friendly: 'You\'re breaking my heart AND wallet! {price}!',
    tough: 'ENOUGH! {price}! Accept or GET OUT!',
    tricky: 'I\'m BLEEDING money! {price}! Take it!',
    generous: 'You WIN! {price} — I\'ll skip dinner tonight!',
  },
};

const FINAL_LINES = {
  friendly: 'Safi habib, {price} tokens. This is truly my LAST price. Wakha?',
  tough: '{price}. Final answer. Take it or bslama.',
  tricky: '{price} tokens — I swear on my children, I cannot go lower!',
  generous: '{price}. Absolute bottom price, wallah!',
};

const WALK_SUCCESS = {
  friendly: 'Wait wait! Come back habib! Okay — {price}! My best wallah!',
  tough: 'Tsk... Fine. Come back. {price}.',
  tricky: 'You\'re bluffing! ...Or not. Fine — {price}!',
  generous: 'Don\'t go! {price} — practically free!',
};

const WALK_FAIL = {
  friendly: 'Bslama habib! Come back tomorrow!',
  tough: 'Fine. Leave. Someone else will buy it.',
  tricky: 'Your loss! This quality won\'t last!',
  generous: 'Okay, maa salama. I\'ll miss you!',
};

const DEAL_LINES = {
  friendly: 'Mabrouk! Bsaha! You\'re a true friend of the souk!',
  tough: 'Hmph. Fine. It\'s yours.',
  tricky: 'Haha! You think you won? Maybe! Bsaha!',
  generous: 'Wonderful! Mabrouk! Tell your friends!',
};

// User options per round — each round introduces new Darija phrases
const DEFAULT_ROUND_OPTIONS = [
  // Round 0 (after merchant opening)
  [
    { id: 'r0_gentle', darija: 'Zwin bzaf, walakin...', english: 'Beautiful, but...', type: 'gentle', icon: '😍' },
    { id: 'r0_moderate', darija: "B-sh7al akhir taman?", english: "What's your best price?", type: 'moderate', icon: '🤔' },
    { id: 'r0_aggressive', darija: 'Ghalia bzaf!', english: 'Too expensive!', type: 'aggressive', icon: '😮' },
  ],
  // Round 1
  [
    { id: 'r1_gentle', darija: 'Shi takhfid?', english: 'Any discount?', type: 'gentle', icon: '💰' },
    { id: 'r1_moderate', darija: '3tini taman mzyan', english: 'Give me a good price', type: 'moderate', icon: '🙏' },
    { id: 'r1_aggressive', darija: 'Hshuma 3lik!', english: 'Shame on you! (playful)', type: 'aggressive', icon: '😤' },
  ],
  // Round 2 (counter-offer unlocked)
  [
    { id: 'r2_gentle', darija: 'Allah y7afdek...', english: 'God bless you...', type: 'gentle', icon: '🤲' },
    { id: 'r2_aggressive', darija: 'Bzzaf bzzaf!', english: 'Way too much!', type: 'aggressive', icon: '✋' },
  ],
  // Round 3 (final push)
  [
    { id: 'r3_gentle', darija: 'Nqas shwiya...', english: 'Lower a bit...', type: 'gentle', icon: '📉' },
    { id: 'r3_aggressive', darija: 'La la, ma mkensh!', english: 'No no, impossible!', type: 'aggressive', icon: '🙅' },
  ],
];

// ── Helper: Merchant avatar ──
function MerchantAvatar({ merchant, mood, gameSettings, size = 'sm' }) {
  const sizeClass = size === 'sm' ? 'w-8 h-8' : 'w-12 h-12';
  const textSize = size === 'sm' ? 'text-lg' : 'text-2xl';
  const moodKey = mood > 60 ? 'happy' : mood > 30 ? 'neutral' : mood > 10 ? 'annoyed' : 'angry';
  const moodImg = merchant.mood_images?.[moodKey];

  return (
    <div className={`${sizeClass} rounded-full bg-[#F5F0E8] flex items-center justify-center ${textSize} overflow-hidden flex-shrink-0 ${mood < 30 ? 'animate-mood-shake' : ''}`}>
      {moodImg ? <img src={moodImg} alt="" className="w-full h-full object-cover" /> :
       merchant.image_url && (merchant.image_url.startsWith('http') || merchant.image_url.startsWith('/')) ?
         <img src={merchant.image_url} alt="" className="w-full h-full object-cover" /> :
         <span><GameIcon icon={merchant.image_url && !merchant.image_url.startsWith('/') ? merchant.image_url : (gameSettings.icon_default_merchant || '🧔')} /></span>}
    </div>
  );
}

// ── Main component ──
export default function BargainGame({
  item, merchant, onComplete, onCancel, onDarijaUsed, onLearnWord, playSound, gameSettings = {}
}) {
  const personality = buildPersonality(merchant);
  const pType = merchant.personality || 'friendly';
  const MAX_ROUNDS = gameSettings.bargain_max_rounds || 4;
  const basePrice = item.base_price_tokens || item.price || 100;
  const startPrice = Math.round(basePrice * personality.startMult);
  const minPrice = Math.round(basePrice * 0.70);

  const openings = gameSettings.bargain_openings || DEFAULT_OPENINGS;
  const responses = gameSettings.bargain_responses_conv || DEFAULT_RESPONSES;
  const lateResponses = gameSettings.bargain_late_responses || LATE_RESPONSES;
  const roundOptions = gameSettings.bargain_user_options || DEFAULT_ROUND_OPTIONS;

  // State
  const [chatHistory, setChatHistory] = useState(() => {
    const line = pick(openings[pType] || openings.friendly);
    const text = fillTemplate(line, { item: item.name_english || 'item', price: startPrice, merchant: merchant.name || 'Merchant' });
    return [{ speaker: 'merchant', text }];
  });
  const [currentPrice, setCurrentPrice] = useState(startPrice);
  const [mood, setMood] = useState(80);
  const [currentRound, setCurrentRound] = useState(0);
  const [stage, setStage] = useState('chatting'); // chatting, counter-offer, typing, walkaway-result, final-offer, deal, failed
  const [isTyping, setIsTyping] = useState(false);
  const [phrasesUsed, setPhrasesUsed] = useState(0);
  const [showTutorial, setShowTutorial] = useState(() => {
    try { return !localStorage.getItem('souk_bargain_tutorial_seen'); } catch { return false; }
  });
  const chatEndRef = useRef(null);

  // Auto-scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory, isTyping]);

  const dismissTutorial = () => {
    setShowTutorial(false);
    try { localStorage.setItem('souk_bargain_tutorial_seen', '1'); } catch {}
  };

  const discount = Math.round(((startPrice - currentPrice) / startPrice) * 100);

  const getMoodColor = () => mood > 60 ? '#16A34A' : mood > 30 ? '#D4A843' : '#DC2626';
  const getMoodEmoji = () => mood > 60 ? (gameSettings.mood_emoji_happy || '😊') : mood > 30 ? (gameSettings.mood_emoji_neutral || '😐') : mood > 10 ? (gameSettings.mood_emoji_annoyed || '😠') : (gameSettings.mood_emoji_angry || '🤬');

  // Calculate price drop + mood cost based on choice type
  const calcEffect = useCallback((type, round) => {
    const diminish = Math.max(0.4, 1 - round * 0.15);
    const randomize = (v) => v * (0.85 + Math.random() * 0.3);
    let baseDrop, baseMood;

    switch (type) {
      case 'gentle':
        baseDrop = personality.dropEasy;
        baseMood = personality.moodLoss * 0.3;
        break;
      case 'moderate':
      case 'counter':
        baseDrop = (personality.dropEasy + personality.dropHard) / 2;
        baseMood = personality.moodLoss * 0.6;
        break;
      case 'aggressive':
        baseDrop = personality.dropHard;
        baseMood = personality.moodLoss;
        break;
      default:
        baseDrop = personality.dropEasy;
        baseMood = personality.moodLoss * 0.3;
    }
    return {
      drop: randomize(baseDrop * diminish),
      moodLoss: Math.round(randomize(baseMood * diminish)),
    };
  }, [personality]);

  // Get merchant response text
  const getMerchantLine = useCallback((choiceType, newPrice, counterAmount) => {
    const vars = { price: newPrice, counter: counterAmount || '', item: item.name_english || 'item' };
    const isLate = currentRound >= 2;

    // For late rounds, sometimes use urgent variants
    if (isLate && Math.random() < 0.5) {
      const late = lateResponses[choiceType === 'moderate' ? 'gentle' : choiceType];
      if (late) {
        const line = late[pType] || late.friendly;
        if (line) return fillTemplate(line, vars);
      }
    }

    const pool = responses[choiceType]?.[pType] || responses.gentle?.friendly;
    if (!pool) return `${newPrice} tokens.`;
    const line = Array.isArray(pool) ? pick(pool) : pool;
    return fillTemplate(line, vars);
  }, [currentRound, pType, responses, lateResponses, item.name_english]);

  // Handle user picking a dialogue option
  const handleChoice = (option) => {
    if (isTyping || stage !== 'chatting') return;
    setPhrasesUsed(p => p + 1);
    onDarijaUsed?.();
    onLearnWord?.(option.darija, option.english, '', 'phrase');

    // Add user message to chat
    setChatHistory(prev => [...prev, {
      speaker: 'user',
      darija: option.darija,
      english: option.english,
      icon: option.icon,
    }]);

    playSound?.('bargain');
    setIsTyping(true);

    // Calculate new price and mood
    const { drop, moodLoss } = calcEffect(option.type, currentRound);
    const newPrice = Math.max(minPrice, Math.round(currentPrice * (1 - drop)));
    const newMood = Math.max(0, mood - moodLoss);
    const nextRound = currentRound + 1;

    setTimeout(() => {
      setIsTyping(false);
      setCurrentPrice(newPrice);
      setMood(newMood);
      setCurrentRound(nextRound);

      // Check if mood exhausted or max rounds reached
      if (newMood < 15 || nextRound >= MAX_ROUNDS) {
        const finalLine = fillTemplate(
          (gameSettings.bargain_final_lines || FINAL_LINES)[pType] || FINAL_LINES.friendly,
          { price: newPrice, item: item.name_english || 'item' }
        );
        setChatHistory(prev => [...prev, { speaker: 'merchant', text: finalLine }]);
        setStage('final-offer');
      } else {
        const merchantLine = getMerchantLine(option.type, newPrice);
        setChatHistory(prev => [...prev, { speaker: 'merchant', text: merchantLine }]);
      }
    }, 800);
  };

  // Handle counter-offer submission
  const handleCounterOffer = (counterPrice) => {
    if (isTyping) return;
    setPhrasesUsed(p => p + 1);
    onDarijaUsed?.();
    onLearnWord?.('B-sh7al?', 'How much?', '', 'phrase');

    const counterDarija = `${counterPrice} barka!`;
    setChatHistory(prev => [...prev, {
      speaker: 'user',
      darija: counterDarija,
      english: `How about ${counterPrice} tokens?`,
      icon: '💰',
    }]);

    playSound?.('bargain');
    setIsTyping(true);
    setStage('chatting');

    // Counter aggressiveness determines drop
    const ratio = counterPrice / currentPrice;
    const counterType = ratio > 0.82 ? 'gentle' : ratio > 0.68 ? 'moderate' : 'aggressive';
    const { drop, moodLoss } = calcEffect(counterType, currentRound);

    const newPrice = Math.max(minPrice, Math.round(currentPrice * (1 - drop)));
    const newMood = Math.max(0, mood - moodLoss);
    const nextRound = currentRound + 1;

    setTimeout(() => {
      setIsTyping(false);
      setCurrentPrice(newPrice);
      setMood(newMood);
      setCurrentRound(nextRound);

      if (newMood < 15 || nextRound >= MAX_ROUNDS) {
        const finalLine = fillTemplate(
          (gameSettings.bargain_final_lines || FINAL_LINES)[pType] || FINAL_LINES.friendly,
          { price: newPrice }
        );
        setChatHistory(prev => [...prev, { speaker: 'merchant', text: finalLine }]);
        setStage('final-offer');
      } else {
        const merchantLine = getMerchantLine('counter', newPrice, counterPrice);
        setChatHistory(prev => [...prev, { speaker: 'merchant', text: merchantLine }]);
      }
    }, 800);
  };

  // Handle walk-away
  const handleWalkaway = () => {
    if (isTyping) return;
    onLearnWord?.('Safi, nmshi', "OK, I'm leaving", '', 'phrase');
    setChatHistory(prev => [...prev, {
      speaker: 'user',
      darija: 'Safi, nmshi...',
      english: "OK, I'm leaving...",
      icon: '🚶',
    }]);

    playSound?.('walkaway');
    setIsTyping(true);

    const success = Math.random() < personality.walkSuccessRate;

    setTimeout(() => {
      setIsTyping(false);
      if (success) {
        const bestPrice = Math.max(minPrice, Math.round(currentPrice * 0.9));
        setCurrentPrice(bestPrice);
        const line = fillTemplate(
          (gameSettings.bargain_walk_success || WALK_SUCCESS)[pType] || WALK_SUCCESS.friendly,
          { price: bestPrice }
        );
        setChatHistory(prev => [...prev, { speaker: 'merchant', text: line }]);
        setStage('walkaway-result');
      } else {
        const line = (gameSettings.bargain_walk_fail || WALK_FAIL)[pType] || WALK_FAIL.friendly;
        setChatHistory(prev => [...prev, { speaker: 'merchant', text: line }]);
        setStage('failed');
      }
    }, 1000);
  };

  // Handle accept deal
  const handleAcceptDeal = () => {
    onLearnWord?.('Wakha!', 'OK! Deal!', '', 'phrase');
    setChatHistory(prev => [...prev, {
      speaker: 'user',
      darija: 'Wakha, deal!',
      english: 'OK, deal!',
      icon: '🤝',
    }]);

    playSound?.('coin');
    setIsTyping(true);

    setTimeout(() => {
      setIsTyping(false);
      const line = (gameSettings.bargain_deal_lines || DEAL_LINES)[pType] || DEAL_LINES.friendly;
      setChatHistory(prev => [...prev, { speaker: 'merchant', text: line }]);
      setStage('deal');

      setTimeout(() => {
        onComplete({
          item,
          finalPrice: currentPrice,
          originalPrice: startPrice,
          saved: startPrice - currentPrice,
          phrasesUsed,
        });
      }, 1200);
    }, 600);
  };

  // Get current user options
  const currentOptions = (currentRound < roundOptions.length)
    ? roundOptions[currentRound]
    : roundOptions[roundOptions.length - 1];

  // Counter-offer preset prices
  const counterPrices = [
    { label: gameSettings.counter_conservative || 'Fair offer', price: Math.round(currentPrice * 0.88), type: 'conservative' },
    { label: gameSettings.counter_bold || 'Bold offer', price: Math.round(currentPrice * 0.75), type: 'bold' },
    { label: gameSettings.counter_aggressive || 'Aggressive!', price: Math.round(currentPrice * 0.62), type: 'aggressive' },
  ];

  // All tutorial phrases (for tutorial overlay)
  const allPhrases = roundOptions.flat();

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-2 sm:p-3 z-[60] animate-modal-overlay">
      {/* First-time tutorial overlay */}
      {showTutorial && (
        <div className="fixed inset-0 bg-black/70 z-[70] flex items-center justify-center p-4" onClick={dismissTutorial}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-fade-up" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 text-center mb-1">{gameSettings.bargain_tutorial_title || 'Bargaining Guide'}</h3>
            <p className="text-xs text-gray-500 text-center mb-4">{gameSettings.bargain_tutorial_subtitle || 'Use Darija phrases to negotiate the best price!'}</p>
            <div className="space-y-2 mb-4">
              {allPhrases.slice(0, 5).map(p => (
                <div key={p.id} className="flex items-center gap-3 bg-[#F5F0E8] rounded-xl p-2.5">
                  <span className="text-lg">{p.icon}</span>
                  <div>
                    <p className="text-sm font-bold text-[#2D6A4F]">{p.darija}</p>
                    <p className="text-xs text-gray-600">{p.english}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-amber-50 rounded-xl p-3 mb-4 border border-amber-200">
              <p className="text-xs text-amber-800">
                {gameSettings.bargain_tutorial_tip || 'Tip: Each round gives you new phrases. You can also make counter-offers and walk away!'}
              </p>
            </div>
            <button onClick={dismissTutorial} className="w-full py-3 rounded-xl bg-[#2D6A4F] hover:bg-[#40916C] text-white font-bold text-sm transition-colors">
              {gameSettings.bargain_tutorial_button || "Yallah! Let's bargain! →"}
            </button>
          </div>
        </div>
      )}

      {/* Main bargaining card */}
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[92vh] flex flex-col animate-modal-content">
        {/* Header: merchant info + price + mood */}
        <div className="p-3 sm:p-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="relative">
              <MerchantAvatar merchant={merchant} mood={mood} gameSettings={gameSettings} size="lg" />
              <span className="absolute -bottom-0.5 -right-0.5 text-sm bg-white rounded-full w-5 h-5 flex items-center justify-center border shadow-sm">
                <GameIcon icon={getMoodEmoji()} />
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-gray-900 text-sm">{merchant.name || merchant.name_english || 'Merchant'}</h3>
              <div className="flex items-center gap-1.5 mt-0.5">
                <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden max-w-[80px]">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${mood}%`, backgroundColor: getMoodColor() }} />
                </div>
                <span className="text-[10px] font-medium" style={{ color: getMoodColor() }}>
                  {mood > 60 ? (gameSettings.mood_happy || 'Happy') : mood > 30 ? (gameSettings.mood_neutral || 'Neutral') : (gameSettings.mood_annoyed || 'Annoyed')}
                </span>
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              {discount > 0 && <p className="text-[10px] text-gray-400 line-through"><GameIcon icon={gameSettings.icon_token || '🪙'} /> {startPrice}</p>}
              <p className={`text-lg font-bold ${discount > 0 ? 'text-green-600' : 'text-amber-700'}`}>
                <GameIcon icon={gameSettings.icon_token || '🪙'} /> {currentPrice}
              </p>
              {discount > 0 && <p className="text-[10px] text-green-600 font-semibold">-{discount}%</p>}
            </div>
            <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 text-lg ml-1">✕</button>
          </div>

          {/* Item being bargained for */}
          <div className="flex items-center gap-2 mt-2.5 bg-gray-50 rounded-lg px-2.5 py-2">
            <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center text-lg overflow-hidden flex-shrink-0">
              {item.image_url && (item.image_url.startsWith('http') || item.image_url.startsWith('/')) ? (
                <img src={item.image_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-base"><GameIcon icon={item.image_url && !item.image_url.startsWith('/') ? item.image_url : (item.icon || '👕')} /></span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-800 truncate">{item.name_english || item.name}</p>
              {item.name_darija && <p className="text-[10px] text-[#2D6A4F] truncate">{item.name_darija}</p>}
            </div>
          </div>
        </div>

        {/* Chat area (scrollable) */}
        <div className="flex-1 overflow-y-auto px-3 sm:px-4 py-3 space-y-2.5 min-h-0" style={{ maxHeight: '40vh' }}>
          {chatHistory.map((msg, i) => (
            msg.speaker === 'merchant' ? (
              // Merchant message — left aligned
              <div key={i} className="flex items-start gap-2 animate-fade-up">
                <MerchantAvatar merchant={merchant} mood={mood} gameSettings={gameSettings} size="sm" />
                <div className="bg-[#F5F0E8] rounded-2xl rounded-tl-sm px-3 py-2 max-w-[80%]">
                  <p className="text-sm text-gray-700 leading-relaxed">{msg.text}</p>
                </div>
              </div>
            ) : (
              // User message — right aligned
              <div key={i} className="flex justify-end animate-fade-up">
                <div className="bg-[#2D6A4F] rounded-2xl rounded-tr-sm px-3 py-2 max-w-[80%]">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm">{msg.icon}</span>
                    <p className="text-sm font-bold text-white">{msg.darija}</p>
                  </div>
                  <p className="text-[11px] text-white/70 mt-0.5">{msg.english}</p>
                </div>
              </div>
            )
          ))}

          {/* Typing indicator */}
          {isTyping && (
            <div className="flex items-start gap-2 animate-fade-up">
              <MerchantAvatar merchant={merchant} mood={mood} gameSettings={gameSettings} size="sm" />
              <div className="bg-[#F5F0E8] rounded-2xl rounded-tl-sm px-4 py-3">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* User input area */}
        <div className="px-3 sm:px-4 pb-3 sm:pb-4 pt-2 border-t border-gray-100 flex-shrink-0">

          {/* Normal chatting — show phrase options */}
          {stage === 'chatting' && !isTyping && (
            <>
              <p className="text-[10px] text-gray-400 mb-1.5 font-medium">
                {gameSettings.bargain_your_response || 'Your response:'} ({(gameSettings.bargain_round_label || 'Round {N}/{M}').replace('{N}', currentRound + 1).replace('{M}', MAX_ROUNDS)})
              </p>
              <div className="grid grid-cols-2 gap-1.5 mb-2">
                {currentOptions.map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => handleChoice(opt)}
                    className="p-2 rounded-xl border border-gray-200 bg-white hover:border-[#2D6A4F] hover:shadow-sm text-left transition-all"
                  >
                    <div className="flex items-center gap-1 mb-0.5">
                      <span className="text-sm">{opt.icon}</span>
                      <span className="text-[11px] font-bold text-[#2D6A4F] flex-1 leading-tight">{opt.darija}</span>
                      <PlayAudio url={opt.audio_url} size="xs" />
                    </div>
                    <p className="text-[10px] text-gray-500 leading-tight">{opt.english}</p>
                  </button>
                ))}
              </div>
              <div className="flex gap-1.5">
                {/* Accept deal */}
                <button
                  onClick={handleAcceptDeal}
                  className="flex-1 py-2 rounded-xl bg-[#2D6A4F] hover:bg-[#40916C] text-white font-bold text-xs transition-colors"
                >
                  🤝 {(gameSettings.bargain_accept || 'Deal! 🪙 {price}').replace('{price}', currentPrice)}
                </button>
                {/* Counter-offer (available from round 1) */}
                {currentRound >= 1 && (
                  <button
                    onClick={() => setStage('counter-offer')}
                    className="px-3 py-2 rounded-xl border-2 border-amber-300 text-amber-700 font-bold text-xs hover:bg-amber-50 transition-colors"
                  >
                    💰 {gameSettings.bargain_counter_btn || 'Counter'}
                  </button>
                )}
                {/* Walk-away (available from round 1) */}
                {currentRound >= 1 && (
                  <button
                    onClick={handleWalkaway}
                    className="px-3 py-2 rounded-xl border-2 border-red-200 text-red-600 font-bold text-xs hover:bg-red-50 transition-colors"
                  >
                    🚶
                  </button>
                )}
              </div>
            </>
          )}

          {/* Counter-offer view */}
          {stage === 'counter-offer' && !isTyping && (
            <>
              <p className="text-xs font-semibold text-gray-600 mb-2">{gameSettings.bargain_counter_title || 'Make a counter-offer:'}</p>
              <div className="space-y-1.5 mb-2">
                {counterPrices.map(cp => (
                  <button
                    key={cp.type}
                    onClick={() => handleCounterOffer(cp.price)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl border transition-all ${
                      cp.type === 'conservative' ? 'border-green-200 bg-green-50 hover:border-green-400' :
                      cp.type === 'bold' ? 'border-amber-200 bg-amber-50 hover:border-amber-400' :
                      'border-red-200 bg-red-50 hover:border-red-400'
                    }`}
                  >
                    <div>
                      <p className="text-xs font-bold text-gray-800">{cp.label}</p>
                      <p className="text-[10px] text-gray-500">
                        {cp.type === 'conservative' ? (gameSettings.counter_safe_desc || 'Safe — merchant likely accepts') :
                         cp.type === 'bold' ? (gameSettings.counter_bold_desc || 'Risky — might work!') :
                         (gameSettings.counter_aggressive_desc || 'Very risky — big mood hit!')}
                      </p>
                    </div>
                    <span className="text-sm font-bold text-amber-700"><GameIcon icon={gameSettings.icon_token || '🪙'} /> {cp.price}</span>
                  </button>
                ))}
              </div>
              <button
                onClick={() => setStage('chatting')}
                className="w-full py-2 rounded-xl border-2 border-gray-200 text-gray-500 font-semibold text-xs hover:border-gray-300 transition-colors"
              >
                ← {gameSettings.bargain_back_phrases || 'Back to phrases'}
              </button>
            </>
          )}

          {/* Final offer — take it or leave it */}
          {stage === 'final-offer' && !isTyping && (
            <div className="space-y-2">
              <p className="text-xs text-center text-gray-500 font-medium">{gameSettings.bargain_final_msg || 'Final price — take it or leave it!'}</p>
              <div className="flex gap-2">
                <button onClick={handleAcceptDeal} className="flex-1 py-3 rounded-xl bg-[#2D6A4F] hover:bg-[#40916C] text-white font-bold text-sm">
                  🤝 {(gameSettings.bargain_deal_button || 'Deal! 🪙 {price}').replace('{price}', currentPrice)}
                </button>
                <button onClick={onCancel} className="px-5 py-3 rounded-xl border-2 border-gray-200 text-gray-600 font-semibold text-sm">
                  {gameSettings.bargain_leave_button || 'Leave'}
                </button>
              </div>
            </div>
          )}

          {/* Walk-away result */}
          {stage === 'walkaway-result' && !isTyping && (
            <div className="space-y-2">
              <p className="text-xs text-center text-green-600 font-semibold">{gameSettings.bargain_walk_comeback || 'The merchant called you back!'}</p>
              <button onClick={handleAcceptDeal} className="w-full py-3 rounded-xl bg-green-600 hover:bg-green-700 text-white font-bold text-sm">
                🤝 {(gameSettings.bargain_buy_at || 'Great deal! 🪙 {price}').replace('{price}', currentPrice)}
              </button>
            </div>
          )}

          {/* Failed — merchant let you go */}
          {stage === 'failed' && !isTyping && (
            <div className="text-center py-2 space-y-2">
              <p className="text-sm text-gray-600">{gameSettings.bargain_walked_away || 'The merchant let you walk away...'}</p>
              <button onClick={onCancel} className="px-6 py-2.5 rounded-xl border-2 border-gray-200 text-gray-600 font-semibold text-sm">
                {gameSettings.bargain_leave_shop || 'Leave Shop'}
              </button>
            </div>
          )}

          {/* Deal done! */}
          {stage === 'deal' && !isTyping && (
            <div className="text-center py-3">
              <span className="text-3xl block mb-1">🤝</span>
              <p className="text-base font-bold text-[#2D6A4F]">{gameSettings.bargain_success_title || 'Deal!'}</p>
              {discount > 0 && (
                <p className="text-xs text-gray-500 mt-0.5">{(gameSettings.bargain_saved_text || 'You saved {percent}% ({tokens} Tokens)').replace('{percent}', discount).replace('{tokens}', startPrice - currentPrice)}</p>
              )}
            </div>
          )}

          {/* Typing state — hide options */}
          {isTyping && (
            <p className="text-xs text-gray-400 text-center py-2">{merchant.name || 'Merchant'} {gameSettings.bargain_typing || 'is thinking...'}</p>
          )}
        </div>
      </div>
    </div>
  );
}
